import React, { useRef } from 'react';
import cx from 'classnames';
import AmiraIconButton, { ICON_BUTTON_TYPES } from '../Global/AmiraIconButton2/AmiraIconButton2';
import { BLANK_TOKEN } from './modularMultipleChoiceConstants';
import { useShrinkToFitPromptText } from './promptTextScaling';

// Both branches below cap the glued run at this many characters, so a single nowrap unit can
// never grow wide enough to overflow .modularMultipleChoice__promptText — applyShrinkToFit only
// measures scrollHeight, so a too-*wide* nowrap box wouldn't trigger a font-size correction.
const MAX_GLUED_RUN_CHARS = 4;

// Text directly attached to the blank (no whitespace between) is already one visual unit with
// it, so glue it whatever it is — this is what catches "[BLANK]." and "[BLANK]'s" alike.
const ATTACHED_RUN = new RegExp(`^\\S{1,${MAX_GLUED_RUN_CHARS}}(?=\\s|$)`);

// Trailing punctuation separated from the blank by whitespace: glue only unambiguous *closing*
// marks — written as \u escapes so a bad re-encode fails loudly instead of silently dropping a
// character. A straight quote/apostrophe here could just as easily be opening the next word (e.g.
// `[BLANK] "corre"`), and a character class can't tell open from close — so those are excluded
// on this branch. (They still glue via ATTACHED_RUN above when there's no space to be ambiguous about.)
const SPACED_TRAILING_PUNCTUATION = new RegExp(
  `^\\s+[.,;:!?)\\]}\\u201D\\u2019\\u00BB\\u2026]{1,${MAX_GLUED_RUN_CHARS}}(?=\\s|$)`
);

/**
 * Render prompt text, turning each BLANK_TOKEN into a styled underline span (a fill-in-the-blank gap).
 * Splitting and rendering as React nodes keeps the text escaped — no dangerouslySetInnerHTML.
 * Plain strings (no token) pass straight through.
 *
 * The blank span is `display: inline-block`, an atomic inline-level box. Per CSS line-breaking
 * rules, atomic inline boxes get a soft-wrap opportunity on both sides of the box itself,
 * regardless of adjacent whitespace — so trailing punctuation right after a blank (e.g. "___.")
 * can still be orphaned onto its own line even with zero space between them. Wrapping the blank
 * and its glued run (attached text or trailing punctuation) in one `white-space: nowrap` unit
 * removes that wrap opportunity.
 */
function renderQuestionText(questionText) {
  const text = String(questionText ?? '');
  if (!text.includes(BLANK_TOKEN)) {
    return text;
  }
  const rawSegments = text.split(BLANK_TOKEN);
  const nodes = [rawSegments[0]];
  for (let index = 1; index < rawSegments.length; index += 1) {
    const blank = <span key={`blank-${index}`} className="modularMultipleChoice__promptBlank" aria-label="blank" />;
    const segment = rawSegments[index];
    const glueMatch = segment.match(ATTACHED_RUN) || segment.match(SPACED_TRAILING_PUNCTUATION);
    if (glueMatch) {
      const gluedRun = glueMatch[0]; // keep the space — nowrap suppresses the break, no need to drop it
      nodes.push(
        <span key={`glue-${index}`} className="modularMultipleChoice__promptBlankGlue">
          {blank}
          {gluedRun}
        </span>
      );
      nodes.push(segment.slice(gluedRun.length));
    } else {
      nodes.push(blank);
      nodes.push(segment);
    }
  }
  return nodes;
}

export default function MmcPromptBar({
  hidePrompt,
  hideRepeat,
  questionText,
  imageURL,
  hideImage,
  containerHeightPx,
  repeatIconSize,
  onRepeatClick,
  repeatDisabled,
  showRepeatPointer,
}) {
  // Whitespace-only text (common on image-clue vocabulary items) counts as no text.
  const hasText = String(questionText ?? '').trim() !== '';
  const showImage = !!imageURL && !hideImage;
  const hasContent = hasText || showImage;
  const promptTextRef = useRef(null);
  useShrinkToFitPromptText(questionText, hasText && !hidePrompt, promptTextRef, containerHeightPx);

  return (
    <div className={cx('modularMultipleChoice__prompt', { hidden: hidePrompt, 'modularMultipleChoice__prompt--bare': !hasContent })}>
      <div className={cx('modularMultipleChoice__repeatBtn', { hidden: hideRepeat })}>
        <AmiraIconButton
          {...repeatIconSize}
          onClick={onRepeatClick}
          disabled={repeatDisabled}
          type={ICON_BUTTON_TYPES.REPEAT_AUDIO}
          secondary
          pointerPosition="left"
          showPointer={showRepeatPointer}
        />
      </div>
      {hasContent && !hidePrompt && (
        <>
          {showImage ? (
            <span className="modularMultipleChoice__promptContent">
              {hasText && (
                <span className="modularMultipleChoice__promptText" ref={promptTextRef}>{renderQuestionText(questionText)}</span>
              )}
              <img className="modularMultipleChoice__promptImage" src={imageURL} alt="Multiple choice prompt" />
            </span>
          ) : (
            <span className="modularMultipleChoice__promptText" ref={promptTextRef}>{renderQuestionText(questionText)}</span>
          )}
          <div className="modularMultipleChoice__promptEndCap" aria-hidden="true" />
        </>
      )}
    </div>
  );
}
